import { supabase } from '../lib/supabase';

export const teamService = {
  async getTeamMembers(companyId = null) {
    try {
      let query = supabase?.from('company_employees')?.select(`
          *,
          user_profile:user_profiles!company_employees_user_id_fkey(
            id, email, full_name
          ),
          supervisor:user_profiles!company_employees_supervisor_id_fkey(
            id, email, full_name
          )
        `)?.eq('status', 'active');

      if (companyId) {
        query = query?.eq('company_id', companyId);
      }

      const { data, error } = await query;
      if (error) throw error;

      return data?.map(employee => ({
        id: employee?.id,
        employeeId: employee?.employee_id,
        userId: employee?.user_id,
        position: employee?.position,
        department: employee?.department,
        hireDate: employee?.hire_date,
        status: employee?.status,
        permissions: employee?.permissions || [],
        licenseNumber: employee?.license_number,
        notes: employee?.notes,
        companyId: employee?.company_id,
        supervisorId: employee?.supervisor_id,
        createdAt: employee?.created_at,
        updatedAt: employee?.updated_at,
        userProfile: employee?.user_profile ? {
          id: employee?.user_profile?.id,
          email: employee?.user_profile?.email,
          fullName: employee?.user_profile?.full_name
        } : null,
        supervisor: employee?.supervisor ? {
          id: employee?.supervisor?.id,
          email: employee?.supervisor?.email,
          fullName: employee?.supervisor?.full_name
        } : null
      })) || [];

    } catch (error) {
      console.error('Error fetching team members:', error);
      throw error;
    }
  },

  async getTeamPerformance(filters = {}) {
    try {
      // Get team members with their task statistics
      const teamMembers = await this.getTeamMembers();
      
      const performanceData = await Promise.all(
        teamMembers?.map(async (member) => {
          try {
            let taskQuery = supabase?.from('ipm_tasks')?.select('*')?.eq('assigned_to', member?.userId);

            if (filters?.dateFrom) {
              taskQuery = taskQuery?.gte('created_at', filters?.dateFrom);
            }

            if (filters?.dateTo) {
              taskQuery = taskQuery?.lte('created_at', filters?.dateTo);
            }

            const { data: tasks, error } = await taskQuery;
            if (error) throw error;

            const totalTasks = tasks?.length || 0;
            const completedTasks = tasks?.filter(task => task?.status === 'completed')?.length || 0;
            const inProgressTasks = tasks?.filter(task => task?.status === 'in_progress')?.length || 0;
            const pendingTasks = tasks?.filter(task => task?.status === 'pending')?.length || 0;
            const overdueTasks = tasks?.filter(task => 
              task?.due_date && new Date(task.due_date) < new Date() && task?.status !== 'completed'
            )?.length || 0;

            const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
            
            const avgDuration = tasks?.filter(task => task?.estimated_duration)
              ?.reduce((sum, task) => sum + task?.estimated_duration, 0) / totalTasks || 0;

            // Calculate quality score based on completion rate, timeliness, etc.
            const timelinessScore = totalTasks > 0 ? Math.max(0, 100 - (overdueTasks / totalTasks * 100)) : 100;
            const qualityScore = Math.round((completionRate + timelinessScore) / 2) / 20; // Convert to 0-5 scale

            return {
              member,
              performance: {
                totalTasks,
                completedTasks,
                inProgressTasks,
                pendingTasks,
                overdueTasks,
                completionRate,
                averageDuration: Math.round(avgDuration),
                qualityScore: Math.min(5, Math.max(0, qualityScore)),
                efficiencyTrend: completionRate >= 85 ? 'up' : completionRate >= 70 ? 'stable' : 'down',
                points: completedTasks * 10 + (completionRate >= 90 ? 50 : completionRate >= 80 ? 25 : 0)
              }
            };
          } catch (error) {
            console.error(`Error fetching performance for member ${member?.id}:`, error);
            return {
              member,
              performance: {
                totalTasks: 0,
                completedTasks: 0,
                inProgressTasks: 0,
                pendingTasks: 0,
                overdueTasks: 0,
                completionRate: 0,
                averageDuration: 0,
                qualityScore: 0,
                efficiencyTrend: 'stable',
                points: 0
              }
            };
          }
        })
      );

      // Sort by points for leaderboard
      return performanceData?.sort((a, b) => b?.performance?.points - a?.performance?.points);

    } catch (error) {
      console.error('Error fetching team performance:', error);
      throw error;
    }
  },

  async assignTask(taskId, memberId, assignedBy = null) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Update the task assignment
      const { error: taskError } = await supabase?.from('ipm_tasks')?.update({ 
          assigned_to: memberId,
          updated_at: new Date()?.toISOString()
        })?.eq('id', taskId);

      if (taskError) throw taskError;

      // Create task assignment record
      const { data, error } = await supabase?.from('task_assignments')?.insert({
          task_id: taskId,
          assigned_to: memberId,
          assigned_by: assignedBy || user?.id,
          assignment_notes: `Task reassigned via team performance hub`
        })?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        taskId: data?.task_id,
        assignedTo: data?.assigned_to,
        assignedBy: data?.assigned_by,
        assignedAt: data?.assigned_at,
        assignmentNotes: data?.assignment_notes
      };

    } catch (error) {
      console.error('Error assigning task:', error);
      throw error;
    }
  },

  async updateMemberLocation(memberId, newLocation, roomType = 'grow_room') {
    try {
      // This could update a member's current location in a future location tracking table
      // For now, we'll focus on task location updates
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Update any pending/in-progress tasks for this member to the new location
      const { data, error } = await supabase?.from('ipm_tasks')?.update({
          location_name: newLocation,
          room_type: roomType,
          updated_at: new Date()?.toISOString()
        })?.eq('assigned_to', memberId)?.in('status', ['pending', 'in_progress'])?.select();

      if (error) throw error;

      return data;

    } catch (error) {
      console.error('Error updating member location:', error);
      throw error;
    }
  },

  async getWorkloadDistribution() {
    try {
      const { data, error } = await supabase?.from('ipm_tasks')?.select(`
          status,
          priority,
          assigned_to,
          user_profiles!ipm_tasks_assigned_to_fkey(full_name)
        `)?.not('assigned_to', 'is', null);

      if (error) throw error;

      const distribution = {};
      
      data?.forEach(task => {
        const memberName = task?.user_profiles?.full_name || 'Unassigned';
        
        if (!distribution?.[memberName]) {
          distribution[memberName] = {
            total: 0,
            completed: 0,
            inProgress: 0,
            pending: 0,
            high: 0,
            medium: 0,
            low: 0
          };
        }

        distribution[memberName].total++;
        distribution[memberName][task.status.replace('-', '')]++;
        distribution[memberName][task.priority]++;
      });

      return Object.entries(distribution)?.map(([name, stats]) => ({
        member: name,
        ...stats
      }));

    } catch (error) {
      console.error('Error fetching workload distribution:', error);
      throw error;
    }
  }
};